/* eslint-disable react/no-multi-comp */

import copy from 'clipboard-copy';
import Quill from 'quill';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearSharedNotesDraft, submitSharedNotes, updateSharedNotesDraft } from '../../actions';
import {
    EDIT_RESULT_ACCEPTED,
    EDIT_RESULT_CONFLICT,
    EDIT_RESULT_FAILED,
    EDIT_RESULT_PENDING,
    EDIT_RESULT_REJECTED
} from '../../constants';

import RichNotesEditor, { FORMATS, handleNotesLinkClick, textToOpsWithLinks } from './RichNotesEditor';

/**
 * Idle time after the last keystroke before a draft is sent to the owner.
 */
const AUTO_SAVE_MS = 700;

/**
 * @typedef {Object} Props
 * @property {string} ownerId - Participant owning the shared notes.
 * @property {string} permission - 'view' or 'edit' grant for us.
 * @property {number|null} [revision] - Document revision, when known.
 * @property {Object|null} [richText] - Quill document, if any.
 * @property {Function} t - Translation function.
 * @property {string} text - Plain notes text.
 */

/**
 * Read-only view of notes another participant shared. The document comes from
 * that participant, so Quill is limited to the editor's own formats: no images
 * or embeds, and links go through Quill's URL sanitizer.
 *
 * @param {Object} props - The shared document.
 * @param {?Object} props.richText - Optional Quill document.
 * @param {string} props.text - The notes text.
 * @returns {React$Element}
 */
function ReadOnlySharedNotes({ richText, text }) {
    const host = useRef(null);
    const viewer = useRef(null);

    useEffect(() => {
        const mount = host.current;
        const container = document.createElement('div');

        mount.appendChild(container);
        const quillViewer = new Quill(container, { formats: FORMATS,
            readOnly: true });

        viewer.current = quillViewer;
        quillViewer.root?.addEventListener?.('click', handleNotesLinkClick);

        return () => {
            quillViewer.root?.removeEventListener?.('click', handleNotesLinkClick);
            viewer.current = null;
            mount.replaceChildren();
        };
    }, []);

    useEffect(() => {
        try {
            if (richText) {
                // Quill throws midway on an embed it has no blot for; the editor
                // allows none, so keep only text inserts.
                viewer.current.setContents({ ops: richText.ops.filter(op => typeof op.insert === 'string') }, 'silent');
            } else if (text) {
                const ops = textToOpsWithLinks(text);

                viewer.current.setContents({ ops }, 'silent');
            } else {
                viewer.current.setText('', 'silent');
            }
        } catch {
            viewer.current.setText(text || '', 'silent');
        }
    }, [ richText, text ]);

    return (
        <div
            className = 'notes_pane-paper notes_pane-shared'
            ref = { host } />
    );
}

/**
 * Downloads a document as a plain-text file.
 *
 * @param {Object} document - Document to download.
 * @param {string} document.text - Text to download.
 * @param {string} name - File name stem.
 * @returns {void}
 */
function downloadText({ text }, name) {
    const url = URL.createObjectURL(new Blob([ text ], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');

    link.href = url;
    link.download = `${name.replace(/[^a-z0-9_-]/gi, '_').slice(0, 80) || 'notes'}.txt`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * View of notes another participant shared. View-only shares render the
 * sanitized read-only document; an edit grant turns it into a draft editor
 * with an explicit Save that the owner has to accept. The draft lives in
 * redux, so it survives tab switches, unmounts and the second responsive
 * pane instance; an accepted request stays frozen until the authoritative
 * share carrying it arrives.
 *
 * @param {Props} props - The shared document, owner id, grant and revision.
 * @returns {React$Element}
 */
export default function SharedNotesView({ ownerId, permission, revision, richText, t, text }) {
    const dispatch = useDispatch();
    const editRequest = useSelector(state => state['features/notes'].editRequests?.[ownerId]);
    const draft = useSelector(state => state['features/notes'].sharedDrafts?.[ownerId]);
    const [ confirmDiscard, setConfirmDiscard ] = useState(false);
    const [ feedback, setFeedback ] = useState('');
    const autoSaved = useRef(null);
    const canEdit = permission === 'edit';
    const dirty = Boolean(draft);
    const pending = editRequest?.status === EDIT_RESULT_PENDING;

    // 'accepted' but the authoritative share carrying our edit has not arrived
    // yet — the draft stays frozen until it does or the request times out.
    const waiting = editRequest?.status === EDIT_RESULT_ACCEPTED
        && Number.isSafeInteger(editRequest.revision) && revision < editRequest.revision;
    const locked = pending || waiting;

    // An owner update while we hold unsent edits must not replace the draft.
    const ownerMoved = dirty && revision !== draft.baseRevision;

    // While a request is in flight the draft is rebased once it resolves.
    const conflict = (ownerMoved && !locked) || editRequest?.status === EDIT_RESULT_CONFLICT;
    const failed = editRequest?.status === EDIT_RESULT_FAILED;

    const onChange = (nextText, document) => {
        if (!canEdit) {
            return;
        }

        // The editor also reports its mount document; only real edits count.
        // Plain-text shares produce a single-insert document on mount.
        const baseline = richText?.ops ?? (text ? [ { insert: `${text}\n` } ] : null);

        if (!dirty && nextText === text
                && JSON.stringify(document?.ops ?? null) === JSON.stringify(baseline)) {
            return;
        }
        dispatch(updateSharedNotesDraft(ownerId, nextText, document, draft?.baseRevision ?? revision));
    };

    const save = () => {
        if (!dirty || locked || conflict) {
            return;
        }
        dispatch(submitSharedNotes(ownerId, draft.text, draft.richText, draft.baseRevision));
    };

    // Edits travel to the owner on their own shortly after typing stops; the
    // Save button stays for an immediate send or a retry.
    // A failed send is retried only once the draft changes again.
    useEffect(() => {
        if (!canEdit || !dirty || locked || conflict || autoSaved.current === draft) {
            return;
        }
        const timer = setTimeout(() => {
            autoSaved.current = draft;
            save();
        }, AUTO_SAVE_MS);

        return () => clearTimeout(timer);
    }, [ canEdit, conflict, draft, locked ]);

    const discard = () => {
        // Clearing the draft also drops the resolved request, so a conflicted
        // or stale request cannot block the next save.
        dispatch(clearSharedNotesDraft(ownerId));
        setConfirmDiscard(false);
    };

    const copyDraft = async () => {
        try {
            await copy(draft?.text ?? '');
            setFeedback(t('notesPane.copied'));
        } catch {
            setFeedback(t('notesPane.copyFailed'));
        }
    };

    const draftActions = dirty && (
        <>
            <button
                onClick = { copyDraft }
                type = 'button'>{t('notesPane.copyEdits')}</button>
            <button
                onClick = { () => downloadText(draft, `notes-${ownerId}`) }
                type = 'button'>{t('notesPane.downloadEdits')}</button>
        </>
    );

    if (!canEdit) {
        return (
            <div className = 'notes_pane-body'>
                <span className = 'notes_pane-private'>{t('notesPane.viewOnly')}</span>
                <ReadOnlySharedNotes
                    richText = { richText }
                    text = { text } />
                {dirty && (
                    <div
                        aria-label = { t('notesPane.unsentEdits') }
                        className = 'notes_pane-confirm'
                        role = 'group'>
                        <p>{t('notesPane.unsentEdits')}</p>
                        {draftActions}
                        <button
                            className = 'notes_pane-danger'
                            onClick = { discard }
                            type = 'button'>{t('notesPane.discardEdits')}</button>
                    </div>
                )}
                <span
                    aria-live = 'polite'
                    className = 'notes_pane-feedback'
                    role = 'status'>{feedback}</span>
            </div>
        );
    }

    return (
        <div className = 'notes_pane-body'>
            <span className = 'notes_pane-private'>{t('notesPane.canEdit')}</span>
            <RichNotesEditor
                onChange = { onChange }
                richText = { dirty ? draft.richText : richText }
                roomName = { `shared-${ownerId}` }
                t = { t }
                text = { dirty ? draft.text : text } />
            <div
                aria-live = 'polite'
                className = 'notes_pane-edit-status'
                role = 'status'>
                {pending && <span>{t('notesPane.editSending')}</span>}
                {waiting && <span>{t('notesPane.editSaved')}</span>}
                {!pending && conflict && (
                    <>
                        <span>{editRequest?.status === EDIT_RESULT_CONFLICT
                            ? t('notesPane.editConflict')
                            : t('notesPane.ownerUpdated')}</span>
                        <button
                            onClick = { () => setConfirmDiscard(true) }
                            type = 'button'>{t('notesPane.reloadLatest')}</button>
                    </>
                )}
                {!pending && !conflict && editRequest?.status === EDIT_RESULT_REJECTED
                    && <span>{t('notesPane.editRejected')}</span>
                }
                {!pending && !conflict && failed && (
                    <>
                        <span>{t('notesPane.editFailed')}</span>
                        {draftActions}
                        <button
                            onClick = { save }
                            type = 'button'>{t('notesPane.retry')}</button>
                    </>
                )}
            </div>
            <div className = 'notes_pane-edit-actions'>
                <button
                    className = 'notes_pane-primary'
                    disabled = { !dirty || locked || conflict }
                    onClick = { save }
                    type = 'button'>{t('notesPane.saveChanges')}</button>
                <button
                    disabled = { !dirty || locked }
                    onClick = { () => setConfirmDiscard(true) }
                    type = 'button'>{t('notesPane.cancel')}</button>
            </div>
            {confirmDiscard && (
                <div
                    aria-label = { t('notesPane.discardEdits') }
                    className = 'notes_pane-confirm'
                    role = 'group'>
                    <p>{t('notesPane.confirmDiscardEdits')}</p>
                    <button
                        autoFocus = { true }
                        onClick = { () => setConfirmDiscard(false) }
                        type = 'button'>{t('notesPane.cancel')}</button>
                    <button
                        className = 'notes_pane-danger'
                        onClick = { discard }
                        type = 'button'>{t('notesPane.discardEdits')}</button>
                </div>
            )}
        </div>
    );
}
